import React, { ReactNode, createContext, useCallback, useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import LoginRequiredModal from '@/components/Common/Modal/LoginRequiredModal';

export interface LoginRequiredOptions {
  /** 이용하려던 기능 이름. '전화 문의'를 넘기면 '전화 문의를 이용하려면 로그인해 주세요.'가 된다. */
  feature?: string;
  title?: string;
  /** feature로 만들어지는 기본 문구 대신 직접 문구를 지정할 때 사용 */
  description?: ReactNode;
  /** 로그인 후 돌아올 경로. 생략하면 팝업을 띄운 시점의 경로 */
  redirectUrl?: string;
  onCancel?: () => void;
}

interface LoginRequiredContextType {
  showLoginRequired: (options?: LoginRequiredOptions) => void;
  closeLoginRequired: () => void;
}

const DEFAULT_DESCRIPTION = '로그인이 필요한 기능이에요.\n로그인해 주세요.';

const LoginRequiredContext = createContext<LoginRequiredContextType | undefined>(undefined);

export const useLoginRequiredContext = (): LoginRequiredContextType => {
  const context = useContext(LoginRequiredContext);
  if (!context) {
    throw new Error('useLoginRequiredContext must be used within a LoginRequiredProvider');
  }
  return context;
};

/** 받침 유무에 따라 목적격 조사(을/를)를 붙인다. */
const withObjectParticle = (word: string) => {
  const lastCharCode = word.charCodeAt(word.length - 1);
  const isHangulSyllable = lastCharCode >= 0xac00 && lastCharCode <= 0xd7a3;

  if (!isHangulSyllable) {
    return `${word}를`;
  }

  return `${word}${(lastCharCode - 0xac00) % 28 === 0 ? '를' : '을'}`;
};

const buildDescription = ({ description, feature }: LoginRequiredOptions) => {
  if (description) {
    return description;
  }

  return feature ? `${withObjectParticle(feature)} 이용하려면\n로그인해 주세요.` : DEFAULT_DESCRIPTION;
};

const LoginRequiredProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // 닫히는 애니메이션 중에도 문구가 유지되도록 열림 여부와 옵션을 분리해서 관리한다.
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<LoginRequiredOptions>({});

  const showLoginRequired = useCallback((nextOptions: LoginRequiredOptions = {}) => {
    setOptions(nextOptions);
    setIsOpen(true);
  }, []);

  const closeLoginRequired = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleCancel = () => {
    setIsOpen(false);
    options.onCancel?.();
  };

  const handleConfirm = () => {
    setIsOpen(false);
    navigate('/login', {
      state: { redirectUrl: options.redirectUrl || `${location.pathname}${location.search}${location.hash}` },
    });
  };

  return (
    <LoginRequiredContext.Provider value={{ showLoginRequired, closeLoginRequired }}>
      {children}
      <LoginRequiredModal
        isOpen={isOpen}
        title={options.title}
        description={buildDescription(options)}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />
    </LoginRequiredContext.Provider>
  );
};

export { LoginRequiredProvider };
